import {
  Link,
  Avatar,
  Dropmenu,
  LabelGroup,
  SlideDown,
  Typography,
  FlexBox,
} from '@globalmed/atlas-ui';
import cn from 'classnames';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Notifications } from '../Notifications';
import { OnDemandNotifications } from '../OnDemandNotifications';
import styles from './TopPanel.module.css';
import { HelpInfoModal } from '@/components/HelpInfoModal';
import { RoleControl } from '@/components/RoleControl';
import { SelectableList } from '@/components/SelectableList';
import { Stepper } from '@/components/Stepper';
import { useInstitutionOptions } from '@/hooks/useInstitutionOptions';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useInstitutionsStore } from '@/stores/institutions';
import {
  EPresenterStep,
  usePresenterVisitStore,
} from '@/stores/presenterVisit';
import { useUsersStore } from '@/stores/users';
import {
  EExternalLinks,
  ERouterNames,
  EUserRoles,
  IDropMenuItems,
  INavigationItemPlus,
} from '@/types';
import { getLinkToLogin } from '@/utils/getLinkToLogin';
import { getUserInitials, nameFormat } from '@/utils/valuesFormat';
import { Logger, downloadWebRtcLogs } from '@globalmed/atlas-common-api';
import { InstitutionLogoContent } from '@/components/InstitutionLogoContent';

type TProps = {
  headerTitle?: string;
  translatedNavItems?: INavigationItemPlus[];
  activeKey?: string;
};

export const TopPanel = ({
  headerTitle,
  translatedNavItems,
  activeKey,
}: TProps) => {
  const { t } = useTranslation();
  const isSmallScreen = useMediaQuery('(max-width: 576px)');
  const isMediumScreen = useMediaQuery('(max-width: 1280px)');
  const navigate = useNavigate();
  const [helpInfoModalOpen, setHelpInfoModalOpen] = useState(false);
  const [isSlideDownOpen, setIsSlideDownOpen] = useState(false);
  const [isInstitutionPickerOpen, setIsInstitutionPickerOpen] = useState(false);

  const {
    currentInstitution,
    institutionOptions,
    isSuperAdmin,
    onInstitutionChange,
  } = useInstitutionOptions();
  const canChangeInstitution = institutionOptions.length > 1 || isSuperAdmin;

  const {
    userData,
    role,
    allRoles,
    deviceId,
    logout,
    orgAdminOrganizationIds,
    isPresenter,
  } = useUsersStore((state) => ({
    userData: state.userData,
    role: state.role,
    allRoles: state.allRoles,
    deviceId: state.deviceId,
    logout: state.logout,
    orgAdminOrganizationIds: state.getOrgAdminOrganizationIds(),
    isPresenter: state.hasAnyRoles(EUserRoles.PRESENTER),
  }));
  const isDevice = role === EUserRoles.DEVICE;
  const hasNonAdminRole = allRoles.some((item) => item !== EUserRoles.ADMIN);
  const isAdministrativeUser =
    allRoles.includes(EUserRoles.ADMIN) || !!orgAdminOrganizationIds.length;
  const showNotifications =
    !isPresenter && (hasNonAdminRole || !isAdministrativeUser);

  const presenterStep = usePresenterVisitStore((state) => state.step);
  const exitPresenterVisit = usePresenterVisitStore((state) => state.exitVisit);

  const disablePatientScheduling = useInstitutionsStore(
    (state) =>
      !!state.currentInstitution?.configuration?.disablePatientScheduling,
  );
  // Without symptoms collection the visit has a single step.
  const isSymptomsCollectionDisabled = useInstitutionsStore((state) =>
    state.isSymptomsCollectionDisabled(),
  );

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      navigate(getLinkToLogin());
    }
  };

  const handleCallHelpModal = () => {
    setHelpInfoModalOpen(true);
  };

  const allDropmenuItems: IDropMenuItems[] = [
    {
      key: 'account-settings',
      text: t('account-settings.account-settings'),
      icon: 'SettingsGear',
      onClick: () => {
        navigate(ERouterNames.ACCOUNT_SETTINGS);
      },
    },
    {
      key: 'help',
      text: t('help'),
      icon: 'InfoCircle',
      onClick: () => handleCallHelpModal(),
    },
    {
      key: 'export-logs',
      text: t('export-logs'),
      icon: 'Download',
      onClick: () => {
        Logger.GetConsoleLogs();
        void downloadWebRtcLogs();
      },
      hidden: isMediumScreen,
    },
    {
      key: 'privacy-policy',
      text: t('privacy-policy'),
      icon: 'Policy',
      onClick: () => {
        window.open(EExternalLinks.PRIVACY_POLICY, '_blank');
      },
    },
    {
      key: 'line-1',
      text: 'line',
      onClick: () => {},
      hidden: isSmallScreen,
    },
    {
      key: 'logout',
      text: t('first-login.log-out'),
      icon: 'LogOut',
      onClick: handleLogout,
    },
  ];

  const dropmenuItems = allDropmenuItems.filter((item) => !item.hidden);

  const userInitials = getUserInitials(userData?.firstName, userData?.lastName);
  const userName = isDevice
    ? nameFormat(userData?.firstName, deviceId)
    : userData?.firstName &&
      nameFormat(userData?.firstName, userData?.lastName);

  const whitelabel = useInstitutionsStore(
    (state) => state.currentInstitution?.whitelabel,
  );

  const activeTab = useMemo(() => {
    const activeParent = translatedNavItems?.find(
      (item) =>
        item.key === activeKey ||
        item.subItems?.some((subItem) => subItem.key === activeKey),
    );
    return activeParent?.title ?? '';
  }, [translatedNavItems, activeKey]);

  return (
    <div
      className={cn(styles.wrapper, {
        [styles.presenter]: !headerTitle && isPresenter,
      })}
      data-testid="top-panel"
    >
      <HelpInfoModal
        open={helpInfoModalOpen}
        onOpenChange={setHelpInfoModalOpen}
      />
      {headerTitle ? (
        <Typography as="span" fontWeight={700} fontSize="24px">
          {headerTitle}
        </Typography>
      ) : isPresenter ? (
        <>
          <div>
            {presenterStep === EPresenterStep.ENTER_SYMPTOMS && (
              <Link
                preIcon="XClose"
                text={t('common.exit')}
                color="destructive"
                weight="semibold"
                onClick={exitPresenterVisit}
              />
            )}
          </div>
          {!isSymptomsCollectionDisabled && (
            <Stepper
              steps={[
                {
                  id: EPresenterStep.SELECT_PATIENT,
                  title: t('dashboard.select-patient'),
                },
                {
                  id: EPresenterStep.ENTER_SYMPTOMS,
                  title: t('dashboard.enter-symptoms'),
                },
              ]}
              activeStepId={presenterStep}
            />
          )}
        </>
      ) : (
        <div className={styles.header}>
          <InstitutionLogoContent
            whitelabel={whitelabel}
            isPreferBanner={!isSmallScreen}
            onClick={
              isMediumScreen && canChangeInstitution
                ? () => setIsInstitutionPickerOpen(true)
                : undefined
            }
          />
          <SlideDown
            open={isInstitutionPickerOpen}
            onOpenChange={setIsInstitutionPickerOpen}
            title={t('common.select-institution')}
            body={
              <SelectableList
                items={institutionOptions}
                selected={currentInstitution?.id ?? ''}
                aria-label={t('common.select-institution')}
                onChange={(id) => {
                  onInstitutionChange(id);
                  setIsInstitutionPickerOpen(false);
                }}
              />
            }
          />
          {activeTab && (
            <Typography
              as="span"
              fontWeight={600}
              fontSize="24px"
              sx={{ lineHeight: '36px' }}
            >
              {activeTab}
            </Typography>
          )}
        </div>
      )}
      <div className={styles.rightGroup}>
        <RoleControl
          components={{
            [EUserRoles.PROVIDER]: !disablePatientScheduling ? (
              <OnDemandNotifications />
            ) : null,
          }}
          withoutNavigate
        />
        {showNotifications && (
          <div className={styles.bell}>
            <Notifications />
          </div>
        )}
        {!isSmallScreen ? (
          <Dropmenu
            placement="bottom-end"
            trigger={
              <Avatar
                src={userData?.meta?.profilePicture ?? ''}
                text={!userData?.meta?.profilePicture ? userInitials : ''}
                size="xl"
                withFocus
              />
            }
          >
            <Dropmenu.Header>
              <LabelGroup title={userName ?? ''} text={userData?.email ?? ''} />
            </Dropmenu.Header>
            <Dropmenu.Line />
            {dropmenuItems.map((item) => {
              if (item.text === 'line') {
                return <Dropmenu.Line key={item.key} />;
              }
              const { key, text, ...props } = item;
              return (
                <Dropmenu.Item key={key} {...props}>
                  {text}
                </Dropmenu.Item>
              );
            })}
          </Dropmenu>
        ) : (
          <>
            <Avatar
              src={userData?.meta?.profilePicture ?? ''}
              text={!userData?.meta?.profilePicture ? userInitials : ''}
              size="md"
              withFocus
              onClick={() => setIsSlideDownOpen(!isSlideDownOpen)}
            />
            <SlideDown
              title={
                <FlexBox direction="column" gap="4px" alignItems="flex-start">
                  <Typography fontWeight={500}>{userName ?? ''}</Typography>
                  <Typography color={'var(--colors-secondary)'}>
                    {userData?.email ?? ''}
                  </Typography>
                </FlexBox>
              }
              bodySections={[
                {
                  id: 'account-settings',
                  content: (
                    <div className={styles.slideDownBody}>
                      {dropmenuItems
                        .flat()
                        .slice(0, -1)
                        .map((item) => (
                          <Link
                            key={item.key}
                            text={item.text}
                            preIcon={item.icon}
                            onClick={item.onClick}
                            color={'black'}
                          />
                        ))}
                    </div>
                  ),
                },
                {
                  id: 'logout',
                  content: (
                    <div className={styles.slideDownBody}>
                      {dropmenuItems
                        .flat()
                        .slice(-1)
                        .map((item) => (
                          <Link
                            key={item.key}
                            text={item.text}
                            preIcon={item.icon}
                            onClick={item.onClick}
                            color={'black'}
                          />
                        ))}
                    </div>
                  ),
                },
              ]}
              open={isSlideDownOpen}
              onOpenChange={setIsSlideDownOpen}
            />
          </>
        )}
      </div>
    </div>
  );
};
